import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CONSOLE_CATALOGUE,
  CONSOLE_GROUP_META,
  CONSOLE_GROUP_ORDER,
  cardToAction,
  type ConsoleCard,
  type ConsoleGroup,
} from '@/data/consoleCatalogue';
import {
  openControlPort,
  makeCommand,
  makeHello,
  makeAck,
  type ControlCommand,
  type ControlAck,
  type ControlStatus,
  type RosterEntry,
} from '@/events/controlChannel';
import { dispatchControlCommand } from '@/domain/controlDispatch';
import { buildControlRoster } from '@/domain/controlRoster';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { simulationApi } from '@/events/simulationEngine';
import { SCRIPTED_EVENTS } from '@/data/scriptedEvents';
import SearchSelect from '@/components/SearchSelect';
import CustomerSimulator from '@/screens/console/CustomerSimulator';
import { RotateCw, Radio, Crosshair, Clock } from 'lucide-react';
import clsx from 'clsx';

/** The scripted beats as pickable options, time-prefixed so the list reads in
 *  running order and a search for "16:00" or "voicemail" both land. */
const BEAT_OPTIONS = SCRIPTED_EVENTS.map((beat) => ({
  id: beat.id,
  label: `${beat.triggerIso.slice(11, 16)} · ${beat.description}`,
}));

/** Which specific records commands should act on. Every field optional: an empty
 *  aim means each handler falls back to the first record that fits. */
interface Aim {
  studioId?: string;
  artistId?: string;
  customerId?: string;
  bookingId?: string;
  depositId?: string;
}

/**
 * Commands run in the connected app tab, or here when used as a single tab.
 * A command is sent to exactly one destination; local commands are not broadcast.
 */
export default function ControlConsole() {
  const portRef = useRef<ReturnType<typeof openControlPort> | null>(null);
  const brain = useBrainStore();
  const clockIso = useSimulationStore(s => s.clockIso);
  const lastSeen = useRef(0);
  const [globalAck, setGlobalAck] = useState<ControlAck | null>(null);
  const [status, setStatus] = useState<ControlStatus | null>(null);
  const [activeGroup, setActiveGroup] = useState<ConsoleGroup | 'all'>('all');
  const [beatId, setBeatId] = useState<string>(SCRIPTED_EVENTS[0]?.id ?? '');
  const [aim, setAim] = useState<Aim>({});
  const [view, setView] = useState<'commands' | 'simulator'>('commands');
  // Which card each in-flight command came from, and the last answer per card.
  const [outcome, setOutcome] = useState<{
    pending: Record<string, string>;
    byCard: Record<string, ControlAck>;
  }>({ pending: {}, byCard: {} });

  useEffect(() => {
    const port = openControlPort();
    portRef.current = port;
    const unsub = port.subscribe((msg) => {
      if (msg.kind === 'status') {
        lastSeen.current = Date.now();
        setStatus(msg);
      }
      if (msg.kind === 'ack') {
        setOutcome((sent) => {
          const cardId = sent.pending[msg.commandId];
          if (!cardId) return sent;
          const { [msg.commandId]: _done, ...rest } = sent.pending;
          return { pending: rest, byCard: { ...sent.byCard, [cardId]: msg } };
        });
      }
    });
    port.post(makeHello(new Date().toISOString())); // hello ts is display-only, not domain time
    const heartbeat = setInterval(() => {
      if (Date.now() - lastSeen.current > 6000) setStatus(null);
      port.post(makeHello(new Date().toISOString()));
    }, 2000);
    return () => {
      clearInterval(heartbeat);
      unsub();
      port.close();
      portRef.current = null;
    };
  }, []);

  const connected = status !== null;
  const firedIds = new Set(status?.firedEventIds ?? simulationApi.fired());
  const localRoster = useMemo(() => buildControlRoster(), [brain]);
  const roster = status?.roster ?? localRoster;

  const execute = (command: ControlCommand, cardId?: string) => {
    if (status && Date.now() - lastSeen.current <= 6000) {
      if (cardId) setOutcome(sent => ({ ...sent, pending: { ...sent.pending, [command.id]: cardId } }));
      portRef.current?.post(command);
      return;
    }
    if (command.action === 'global:nav' && command.navigateTo) {
      window.location.assign(command.navigateTo);
      return;
    }
    const result = dispatchControlCommand(command.action, command.payload);
    const ack = makeAck({ commandId: command.id, ...result, ts: useSimulationStore.getState().clockIso });
    if (cardId) setOutcome(sent => ({ ...sent, byCard: { ...sent.byCard, [cardId]: ack } }));
    else setGlobalAck(ack);
  };

  /**
   * What each command is aimed at. Empty means "whatever fits" — the handler
   * picks the first suitable record, which is what you want when demoing the
   * shape of a flow. Set one and every command carries it, which is what you
   * want when the client asks "do that for MY booking".
   */
  const aimPayload = useMemo(() => {
    const p: Record<string, unknown> = {};
    if (aim.studioId) p.studioId = aim.studioId;
    if (aim.artistId) p.artistId = aim.artistId;
    if (aim.customerId) p.customerId = aim.customerId;
    if (aim.bookingId) p.bookingId = aim.bookingId;
    if (aim.depositId) p.depositId = aim.depositId;
    return p;
  }, [aim]);

  const aimedCount = Object.keys(aimPayload).length;

  const send = (card: ConsoleCard) => {
    const { action, payload } = cardToAction(card);
    const ts = status?.clockIso ?? clockIso;
    // The card's own payload wins over the aim: a card that says $200 means $200.
    const command = makeCommand({
      action,
      payload: { ...aimPayload, ...payload },
      navigateTo: card.navigateTo,
      ts,
    });
    execute(command, card.id);
  };

  const sendGlobal = (action: string, payload?: Record<string, unknown>) => {
    const ts = status?.clockIso ?? clockIso;
    execute(makeCommand({ action, payload, ts }));
  };

  const totalFired = (status?.counts.fired ?? 0) + (status?.counts.remaining ?? 0);

  return (
    <div className="min-h-screen bg-ink-black text-cream" data-testid="control-console">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="label-small-caps text-gold-bright">Control console</p>
            <h1 className="heading-display text-3xl text-cream mt-2">Demo control console</h1>
            <p className="font-serif text-cream-soft mt-2 max-w-2xl">
              Use this tab on its own, or open the app in another window to send commands there.
            </p>
          </div>
          <div
            className={clsx(
              'flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs',
              connected ? 'border-mint/50 text-mint' : 'border-gold/50 text-gold',
            )}
            data-testid="console-connection"
          >
            <Radio size={13} />
            {connected ? (
              <span>Connected · {status?.clockIso.slice(11, 16)} · {status?.counts.fired}/{totalFired} fired</span>
            ) : (
              <span>Single-tab mode · commands run here</span>
            )}
          </div>
        </header>

        <div className="flex gap-2 mb-6" data-testid="console-view-toggle">
          <GroupChip active={view === 'commands'} label="Commands" onClick={() => setView('commands')} />
          <button
            type="button"
            data-testid="console-view-simulator"
            onClick={() => setView('simulator')}
            className={
              view === 'simulator'
                ? 'rounded-md border px-3 py-1.5 font-inter text-[11px] uppercase tracking-widest border-gold-bright bg-gold/10 text-gold-bright'
                : 'rounded-md border px-3 py-1.5 font-inter text-[11px] uppercase tracking-widest border-gold-dim/30 text-cream-soft hover:border-gold-bright/50'
            }
          >
            Customer Simulator
          </button>
        </div>

        {view === 'simulator' ? <CustomerSimulator /> : null}

        {view === 'commands' ? (
          <>
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="console-globals">
          {/* The demo clock is frozen at the seed day (persistence fast-unlock),
              so the time-jump / play / rollover controls are gone. This re-hydrates
              the connected app's world from the API — a plain reload, not a data
              reset (use "Reset Data" in the app's own panel for that). */}
          <button
            type="button"
            className="btn-quiet text-xs"
            data-testid="console-restore"
            onClick={() => sendGlobal('global:reset')}
          >
            <RotateCw size={12} /> Reload {connected ? 'the connected app' : 'this tab'}
          </button>
        </div>

        {globalAck && <p role="status" className="mb-3 text-sm">{globalAck.ok ? 'Command run.' : globalAck.note ?? 'Command could not run.'}</p>}
        {!connected && <p className="mb-3 text-sm text-cream-soft">Changes are saved in the app. <a href="/" className="underline text-gold">Open the app to inspect them</a>.</p>}
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="console-beat-picker">
          <span className="label-small-caps text-cream-faint">Beat</span>
          <div className="w-full max-w-md">
            <SearchSelect
              options={BEAT_OPTIONS}
              value={beatId}
              onSelect={(id) => setBeatId(id ?? BEAT_OPTIONS[0]?.id ?? '')}
              noun="beat"
              placeholder="Pick a scripted beat"
              testid="console-beat-select"
            />
          </div>
          <button
            type="button"
            className="btn-quiet text-xs"
            onClick={() => sendGlobal('global:beat', { eventId: beatId })}
          >
            Fire beat
          </button>
        </div>

        <section className="panel p-4 mb-6" data-testid="console-aim">
          <div className="flex items-baseline justify-between gap-3 mb-3">
            <div>
              <p className="label-small-caps text-gold-bright flex items-center gap-2">
                <Crosshair size={12} /> Aim
              </p>
              <p className="font-serif text-sm text-cream-soft mt-1">
                {aimedCount === 0
                  ? 'Nothing aimed — each trigger acts on the first record that fits.'
                  : `Every trigger below is aimed at ${aimedCount} chosen ${aimedCount === 1 ? 'record' : 'records'}.`}
              </p>
            </div>
            {aimedCount > 0 ? (
              <button type="button" className="btn-quiet text-xs" onClick={() => setAim({})}>
                Clear aim
              </button>
            ) : null}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <AimSelect label="Studio" options={roster.studios} value={aim.studioId}
              onChange={(v) => setAim((a) => ({ ...a, studioId: v }))} />
            <AimSelect label="Artist" options={roster.artists} value={aim.artistId}
              onChange={(v) => setAim((a) => ({ ...a, artistId: v }))} />
            <AimSelect label="Client" options={roster.customers} value={aim.customerId}
              onChange={(v) => setAim((a) => ({ ...a, customerId: v }))} />
            <AimSelect label="Booking" options={roster.bookings} value={aim.bookingId}
              onChange={(v) => setAim((a) => ({ ...a, bookingId: v }))} />
            <AimSelect label="Deposit" options={roster.deposits} value={aim.depositId}
              onChange={(v) => setAim((a) => ({ ...a, depositId: v }))} />
          </div>
        </section>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="console-group-filter">
          <GroupChip active={activeGroup === 'all'} label="All" onClick={() => setActiveGroup('all')} />
          {CONSOLE_GROUP_ORDER.map((g) => (
            <GroupChip key={g} active={activeGroup === g} label={CONSOLE_GROUP_META[g].label} onClick={() => setActiveGroup(g)} />
          ))}
        </div>

        {CONSOLE_GROUP_ORDER.filter((g) => activeGroup === 'all' || activeGroup === g).map((g) => {
          const cards = CONSOLE_CATALOGUE.filter((c) => c.group === g);
          if (cards.length === 0) return null;
          return (
            <section key={g} className="mb-8" data-testid={`console-section-${g}`}>
              <header className="mb-3">
                <h2 className="font-cinzel text-lg text-cream">{CONSOLE_GROUP_META[g].label}</h2>
                <p className="font-serif text-cream-soft text-sm mt-0.5">{CONSOLE_GROUP_META[g].story}</p>
              </header>
              <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {cards.map((card) => {
                  const fired = card.oneShotEventId ? firedIds.has(card.oneShotEventId) : false;
                  const ack = outcome.byCard[card.id];
                  return (
                    <li key={card.id}>
                      <button
                        type="button"
                        disabled={fired}
                        onClick={() => send(card)}
                        data-testid={`console-card-${card.id}`}
                        className={clsx(
                          'w-full text-left panel transition p-4 h-full flex flex-col gap-2 disabled:opacity-40 disabled:cursor-not-allowed',
                          ack && !ack.ok
                            ? 'border-blood/60 hover:border-blood'
                            : 'hover:border-gold-bright/60',
                        )}
                      >
                        <span className="flex items-start gap-3">
                          <span className="text-2xl shrink-0" aria-hidden="true">{card.emoji}</span>
                          <span className="flex-1 min-w-0">
                            <span className="block font-inter text-base text-cream leading-snug">{card.title}</span>
                            <span className="block font-serif text-sm text-cream-soft mt-1 leading-snug">{card.subtitle}</span>
                          </span>
                        </span>
                        {card.movesClock ? (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-orange">
                            <Clock size={10} /> Moves the clock
                          </span>
                        ) : null}
                        {fired ? <span className="text-[10px] uppercase tracking-widest text-cream-faint">Fired</span> : null}
                        {ack ? (
                          <span
                            className={clsx(
                              'block font-inter text-xs leading-snug',
                              ack.ok ? 'text-mint' : 'text-blood',
                            )}
                            data-testid={`console-ack-${card.id}`}
                          >
                            {ack.ok ? `✓ ${ack.note ?? 'Done'}` : `✗ ${ack.note ?? 'Did not run'}`}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
          </>
        ) : null}
      </div>
    </div>
  );
}

function AimSelect({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: RosterEntry[];
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}) {
  const noun = label.toLowerCase();
  return (
    <div className="flex items-center gap-2 text-xs min-w-0">
      <span className="label-small-caps text-cream-faint w-16 shrink-0">{label}</span>
      <div className="flex-1 min-w-0">
        <SearchSelect
          options={options}
          value={value}
          onSelect={onChange}
          disabled={options.length === 0}
          noun={noun}
          clearLabel={`Any ${noun}`}
          placeholder={options.length === 0 ? 'none available' : `Any ${noun}`}
          testid={`console-aim-${noun}`}
        />
      </div>
    </div>
  );
}

function GroupChip({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'rounded-md border px-3 py-1.5 font-inter text-[11px] uppercase tracking-widest transition',
        active ? 'border-gold-bright bg-gold/10 text-gold-bright' : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/50',
      )}
    >
      {label}
    </button>
  );
}
